import { useState, useMemo, useEffect } from "react";
import { Loader2, Send, AlertTriangle } from "lucide-react";
import { api, extractAxiosError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogFooter } from "./EditPercentagesDialog";
import { AnimatedNumber } from "./AnimatedNumber";
import { wheelMembers } from "../lib/poolMembers";
import { currencySymbol, formatCurrency } from "../contexts/FxContext";
import { DEFAULT_CURRENCY } from "../lib/defaultCurrency";

export function ExecutePayoutDialog({ pool, open, onClose, onExecuted }) {
    const currency = pool?.currency || DEFAULT_CURRENCY;
    const symbol = currencySymbol(currency);
    const fmt = (amount, decimals = 2) => formatCurrency(amount, currency, { decimals });
    const [amount, setAmount] = useState(pool?.next_payout?.amount?.toString() || "");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [balance, setBalance] = useState(null);
    const recipients = useMemo(() => wheelMembers(pool?.members), [pool?.members]);
    const readiness = balance?.payout_readiness;
    const payoutBlocked = readiness && readiness.ready === false;
    const blocking = readiness?.blocking || [];
    const warnings = readiness?.warnings || [];

    useEffect(() => {
        if (!open || !pool?.id) return;
        let mounted = true;
        api.get(`/pools/${pool.id}/balance`)
            .then(({ data }) => { if (mounted) setBalance(data); })
            .catch(() => { if (mounted) setBalance(null); });
        return () => { mounted = false; };
    }, [open, pool?.id]);

    if (!open) return null;

    const numericAmount = parseFloat(amount || "0");
    const available = balance?.available_balance ?? null;
    const overBalance = available != null && numericAmount > available + 0.009;
    const canSubmit = !busy && !overBalance && !payoutBlocked && numericAmount > 0;

    const breakdown = recipients.map((m) => ({
        ...m,
        amount: +(numericAmount * m.percentage / 100).toFixed(2),
    }));

    const submit = async () => {
        if (!numericAmount || numericAmount <= 0) { toast.error("Enter a payout amount"); return; }
        if (payoutBlocked) {
            toast.error("Complete recipient setup before running a payout");
            return;
        }
        if (overBalance) {
            toast.error(`Amount exceeds available balance (${fmt(available)})`);
            return;
        }
        try {
            setBusy(true);
            const { data } = await api.post(`/pools/${pool.id}/payouts`, { total_amount: numericAmount, note });
            if (data.status === "applied") {
                toast.success("Payout executed", { description: `Distributed ${fmt(numericAmount)} across ${recipients.length} recipients` });
            } else {
                toast.success("Payout queued for approval", { description: "An admin must approve before disbursement" });
            }
            onExecuted?.(data);
            onClose();
        } catch (err) {
            toast.error(extractAxiosError(err, "Could not execute payout"));
        } finally { setBusy(false); }
    };

    return (
        <Dialog onClose={onClose} title="Execute payout" subtitle="Each recipient needs bank details on file (UK: sort code + account; US: routing + account; etc.). FairShare members may also need payout setup under Settings → Profile." testid="execute-payout-dialog" size="lg">
            <div className="space-y-4">
                {payoutBlocked && (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.08] p-3.5 space-y-2" data-testid="payout-readiness-blocked">
                        <p className="text-sm font-medium text-amber-200">Recipients not ready for payout</p>
                        <ul className="text-xs text-white/60 list-disc pl-4 space-y-0.5">
                            {blocking.map((item) => (
                                <li key={`${item.code}-${item.member_id || item.name}`}>{item.message}</li>
                            ))}
                        </ul>
                        <p className="text-[11px] text-white/45">
                            Link banks in the Recipients panel. External recipients (Adam, Eve, etc.) do not need FairShare accounts.
                            If you receive a share yourself, finish payout setup under Settings → Profile.
                        </p>
                    </div>
                )}
                {!payoutBlocked && warnings.length > 0 && (
                    <div className="rounded-lg border border-sky-500/20 bg-sky-500/[0.06] p-3 space-y-1" data-testid="payout-readiness-warnings">
                        {warnings.map((item) => (
                            <p key={`${item.code}-${item.member_id || item.name}`} className="text-[11px] text-white/55">{item.message}</p>
                        ))}
                    </div>
                )}
                {available != null && (
                    <div className="rounded-lg border border-[#00BF0D]/20 bg-[#00BF0D]/[0.05] px-3 py-2.5 text-sm">
                        <span className="text-white/55">Available balance: </span>
                        <span className="font-mono font-semibold text-[#00BF0D]">{fmt(available)}</span>
                    </div>
                )}
                <div>
                    <label className="label-eyebrow">Total payout amount ({currency})</label>
                    <div className="mt-1.5 relative">
                        <span className="absolute inset-y-0 left-3.5 flex items-center text-white/45 font-mono text-lg">{symbol}</span>
                        <input
                            type="text" inputMode="decimal" value={amount}
                            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                            className={`input-dark w-full rounded-md pr-3 py-2.5 text-lg font-mono ${symbol.length > 1 ? "pl-10" : "pl-8"}`}
                            data-testid="execute-amount"
                        />
                    </div>
                </div>
                <div>
                    <label className="label-eyebrow">Memo (optional)</label>
                    <input
                        value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="May Q1 distribution"
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid="execute-note"
                    />
                </div>
                {overBalance && (
                    <div className="rounded-lg border border-rose-500/25 bg-rose-500/[0.06] p-3 flex items-start gap-2.5" data-testid="over-balance-warning">
                        <AlertTriangle className="h-4 w-4 text-rose-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-rose-200/90">This amount exceeds the pool&apos;s available balance.</p>
                    </div>
                )}
                {numericAmount > 0 && !payoutBlocked && (
                    <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] divide-y divide-white/[0.04]">
                        {breakdown.map((b) => (
                            <div key={b.id} className="flex items-center justify-between px-3 py-2" data-testid={`execute-breakdown-${b.id}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="h-2 w-2 rounded-full" style={{ background: b.color }} />
                                    <span className="text-sm truncate">{b.name}</span>
                                    <span className="text-[11px] text-white/40 font-mono">{b.percentage}%</span>
                                </div>
                                <div className="text-right">
                                    <p className="font-mono text-sm" style={{ color: b.color }}>
                                        <AnimatedNumber value={b.amount} prefix={symbol} decimals={2} />
                                    </p>
                                    {!b.bank?.account_last4 && (
                                        <p className="text-[10px] text-amber-300/80">Bank required</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <DialogFooter>
                <button onClick={onClose} className="btn-ghost rounded-md px-4 py-2 text-sm">Cancel</button>
                <button onClick={submit} disabled={!canSubmit} className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="execute-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {busy ? "Submitting…" : payoutBlocked ? "Complete recipient setup" : "Submit payout"}
                </button>
            </DialogFooter>
        </Dialog>
    );
}
